/**
 * The row document, the row patch and a patch's refusal as they cross the
 * wire (PLAN §6.1, D2, D6): REST, the change and rundown WS, the site link,
 * the peer link and the production IPC all carry these.
 *
 * Wire copies of `@acp/domain/row.js` and `@acp/domain/rejections.js` (D37: a
 * contract package never reuses a domain schema). `ROW_SHAPE_PROOFS` fails to
 * compile the day a copy and its domain type drift apart; `rowFromWire` and
 * `rowToWire` are where a difference is then converted. Today the shapes are
 * identical, so the mappers are the identity — the proofs are what make that
 * sound, not an assumption.
 */
import type { Row, RowCreate, RowPatch, RowSource } from '@acp/domain/row.js';
import type { PatchRejection } from '@acp/domain/rejections.js';
import { base64Schema, fieldPathSchema, hlcSchema, jsonValueSchema, opaqueIdSchema, ordSchema, ulidSchema } from '@acp/domain/primitives.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { rowKindWireSchema } from './rowFields.js';
import type { Same } from './shapeProofs.js';

/**
 * Where a row came from (D2). An NRCS row carries the MOS ids that identify it
 * upstream; the gateway (§7.6, planned) matches on them.
 */
export const rowSourceWireSchema = z.discriminatedUnion('system', [
  z.object({ system: z.literal('native') }).strict(),
  z
    .object({
      system: z.literal('mos'),
      /** The NRCS's MOS id (`ncsID`). */
      ncsId: z.string().min(1).max(128),
      /** Running order id (`roID`). */
      roId: z.string().min(1).max(128),
      /** Story id, for rows below the running order. */
      storyId: z.string().min(1).max(128).nullable(),
      /** Item id, for rows mapped from a story item. */
      itemId: z.string().min(1).max(128).nullable(),
    })
    .strict(),
]);
type RowSourceWire = z.infer<typeof rowSourceWireSchema>;

function sameKeys(a: Readonly<Record<string, unknown>>, b: Readonly<Record<string, unknown>>): boolean {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => Object.hasOwn(b, k));
}

function structuralCopiesAgree(row: {
  readonly parentId: string | null;
  readonly ord: string;
  readonly deleted: boolean;
  readonly f: Readonly<Record<string, unknown>>;
}): boolean {
  return row.f['parentId'] === row.parentId && row.f['ord'] === row.ord && row.f['deleted'] === row.deleted;
}

/**
 * A row: values `f` and per-field HLCs `h` (open records keyed by field path,
 * so a field a newer writer adds reaches an older reader intact, §6.8), and
 * its metadata. The top-level `parentId`/`ord`/`deleted` must equal their `f`
 * copies (`@acp/domain/row.js` explains why they exist).
 */
export const rowWireSchema = z
  .object({
    rowId: ulidSchema,
    rundownId: ulidSchema,
    kind: rowKindWireSchema,
    parentId: ulidSchema.nullable(),
    ord: ordSchema,
    deleted: z.boolean(),
    f: z.record(fieldPathSchema, jsonValueSchema),
    h: z.record(fieldPathSchema, hlcSchema),
    source: rowSourceWireSchema,
    /** Field paths (or namespace prefixes) only `source.system` may write. */
    lockedFields: z.array(fieldPathSchema),
    text: z.record(fieldPathSchema, base64Schema),
    version: z.number().int().nonnegative(),
    changedAt: ulidSchema,
  })
  .strict()
  .refine((row) => sameKeys(row.f, row.h), { message: 'f and h must name the same fields' })
  .refine(structuralCopiesAgree, { message: 'parentId/ord/deleted must equal f.parentId/f.ord/f.deleted' });
type RowWire = z.infer<typeof rowWireSchema>;

/** The identity and ownership a creation patch carries (D2). */
export const rowCreateWireSchema = z
  .object({
    kind: rowKindWireSchema,
    rundownId: ulidSchema,
    source: rowSourceWireSchema,
    lockedFields: z.array(fieldPathSchema),
  })
  .strict();
type RowCreateWire = z.infer<typeof rowCreateWireSchema>;

/**
 * One patch: set fields of one row under one HLC, plus Yjs `text` updates and
 * the `create` block of a row's first patch (PLAN §6.1). `.strict()`: servers
 * read it as a request, and an unknown key there is a client bug or a skew.
 */
export const rowPatchWireSchema = z
  .object({
    rowId: ulidSchema,
    set: z.record(fieldPathSchema, jsonValueSchema),
    text: z.record(fieldPathSchema, base64Schema).optional(),
    create: rowCreateWireSchema.optional(),
    hlc: hlcSchema,
    /** The writer that produced it (node id, session id), so fan-out can skip the origin. */
    origin: opaqueIdSchema,
    /** Client-generated, so a retried send is recognisable as the same operation. */
    clientOpId: opaqueIdSchema,
  })
  .strict()
  .refine((p) => Object.keys(p.set).length > 0 || Object.keys(p.text ?? {}).length > 0, {
    message: 'a patch must set at least one field or carry a text update',
  });
type RowPatchWire = z.infer<typeof rowPatchWireSchema>;

/** Why a patch was refused: `@acp/domain/rejections.js`' codes, verbatim. */
/** Open: a newer server may refuse a patch for a reason this client does not know; it still reads the rejection. */
const patchRejectionCodeWireSchema = openEnum([
  'unknown-row',
  'incomplete-create',
  'create-mismatch',
  'invalid-create',
  'source-mismatch',
  'row-mismatch',
  'rundown-mismatch',
  'locked-field',
  'text-field-set',
  'unknown-text-field',
  'invalid-text-update',
  'unknown-field',
  'invalid-value',
  'invalid-parent',
  'parent-not-found',
  'parent-kind',
  'parent-rundown',
  'invalid-hlc',
  'invalid-field-path',
  'forbidden',
]);

export const patchRejectionWireSchema = z
  .object({
    code: patchRejectionCodeWireSchema,
    /** The field path concerned, or null for a whole-patch reason. */
    field: z.string().nullable(),
    message: z.string(),
  })
  .strict();
type PatchRejectionWire = z.infer<typeof patchRejectionWireSchema>;

/** The domain row as the wire carries it. */
export function rowToWire(row: Row): RowWire {
  return row;
}

/** A parsed wire row as the domain holds it. */
export function rowFromWire(row: RowWire): Row {
  return row;
}

const rowSame: Same<RowWire, Row> = true;
const sourceSame: Same<RowSourceWire, RowSource> = true;
const createSame: Same<RowCreateWire, RowCreate> = true;
const patchSame: Same<RowPatchWire, RowPatch> = true;
// One way only (D37 §4.3): every domain rejection is a wire rejection, and the wire's code is open — a reader reads a
// newer server's code as `unknown` — so a wire rejection is not a domain one. Nothing maps wire → domain.
const rejectionSame: [PatchRejection] extends [PatchRejectionWire] ? true : false = true;
/** Compile-time proofs that each wire copy above matches its domain type (`shapeProofs.ts`). */
export const ROW_SHAPE_PROOFS = [rowSame, sourceSame, createSame, patchSame, rejectionSame] as const;
